"use client";

import { useState, useEffect, useCallback, useRef } from "react";
import Link from "@/components/Link";
import api from "@/lib/api";
import { ApiRoutes, AppRoutes } from "@/lib/routeResolver";
import { isValidEmail, sanitizePhone } from "@/lib/validators";
import {
  registerWebsiteGuest,
  type DestinationInRegion,
  type DestinationWithPackage,
} from "@/lib/requestQuote";
import ClientQuoteForm from "./ClientQuoteForm";
import { useUtmCodes } from "@/lib/utm/hooks";
import styles from "./request-quote.module.css";

/* ── Types ──────────────────────────────────────────────────────── */

interface EmailCheckData {
  name?: string;
  type?: string;
  id?: string;
  sourceId?: string;
  travelNetworkId?: string;
  hostAgencyId?: string;
  iata?: string;
  clia?: string;
  arc?: string;
  true?: string;
  vtc?: string;
  locationInformation?: Array<{
    addressTitle?: string;
    phone1?: string;
    tnAddress?: {
      full_Address?: string;
      apt_floor?: string;
      streetNumber?: string;
      streetName?: string;
      city?: string;
      state?: string;
      zip?: string;
      country?: string;
      county?: string;
      lat?: number;
      lng?: number;
    };
    contactPersonsList?: Array<{
      contactPersonTitle?: string;
      firstName?: string;
      lastName?: string;
      businessPhone?: string;
      email?: string;
    }>;
  }>;
  [key: string]: unknown;
}

export interface RequestQuoteClientProps {
  destinations: DestinationInRegion[];
  destinationsWithPackages: DestinationWithPackage[];
}

/* ── Stepper config ─────────────────────────────────────────────── */

const STEPS = [
  { title: "Email", subtitle: "Verify your identity" },
  { title: "Account Type", subtitle: "Business or Passenger" },
  { title: "Quote Details", subtitle: "Fill the form" },
  { title: "Confirmation", subtitle: "Request submitted" },
] as const;

/** Map the actual internal step (1-7) to a visual stepper index (0-3). */
function stepToVisualIndex(step: number): number {
  if (step <= 1) return 0;
  if (step <= 4) return 1;
  if (step === 5) return 2;
  return 3;
}

/** Map the current step to the step you go back to. null = no back. */
function getBackStep(step: number): number | null {
  switch (step) {
    case 2:
      return 1;
    case 3:
      return 2;
    case 4:
      return 1; // passenger found — back to email
    case 5:
      return 2; // back to type selection (only for new clients)
    default:
      return null;
  }
}

/* ── Component ──────────────────────────────────────────────────── */

export default function RequestQuoteClient({
  destinations,
  destinationsWithPackages,
}: RequestQuoteClientProps) {
  // Step state
  const [step, setStep] = useState(1);

  // UTM + referrer
  const utmParams = useUtmCodes();
  const [referrer, setReferrer] = useState("");

  // Email step
  const [mainEmail, setMainEmail] = useState("");
  const [emailTouched, setEmailTouched] = useState(false);
  const [emailFetchSent, setEmailFetchSent] = useState(false);
  const [emailFetchFailed, setEmailFetchFailed] = useState(false);
  const [emailData, setEmailData] = useState<EmailCheckData | null>(null);
  const [emailType, setEmailType] = useState<string | null>(null);
  const [clientExist, setClientExist] = useState(false);
  const [guestId, setGuestId] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);

  // Type selection step
  const [selectedCrmType, setSelectedCrmType] = useState<
    "business" | "passenger"
  >("business");

  // Quote form validity (reported by ClientQuoteForm)
  const [quoteFormValid, setQuoteFormValid] = useState(false);

  const sectionRef = useRef<HTMLElement>(null);

  // Adjust stepper sticky top based on whether the site header is fixed
  useEffect(() => {
    const onScroll = () => {
      const stickyHeader = document.querySelector(".sticky-wrapper.sticky");
      const headerH = stickyHeader
        ? stickyHeader.getBoundingClientRect().height
        : 0;
      sectionRef.current?.style.setProperty("--header-h", `${headerH}px`);
    };
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  // Scroll to top on step change
  useEffect(() => {
    window.scrollTo(0, 40);
    setSelectedCrmType("business");
  }, [step]);

  useEffect(() => {
    setReferrer(document.referrer);
  }, []);

  // Email validation
  const emailValid = mainEmail ? isValidEmail(mainEmail) : false;

  // Check email existence
  const checkEmailExists = useCallback(async () => {
    setLoading(true);
    try {
      const res = await api.get(ApiRoutes.CRM_IS_EMAIL_EXIST, {
        params: { Email: mainEmail },
      });
      setEmailFetchSent(true);
      setEmailFetchFailed(false);

      const existing: EmailCheckData | null = res?.data?.data ?? null;
      setGuestId(null);
      registerWebsiteGuest({
        email: mainEmail,
        isPassenger: !!existing && res.data.entityId === "Passenger",
        agentId: existing?.id ?? null,
        existingAgent: !!existing,
      }).then(setGuestId);

      if (existing) {
        setEmailData(existing);
        setEmailType(res.data.entityId);
        setClientExist(true);
        if (res.data.entityId === "Passenger") {
          setStep(4);
        } else if (res.data.entityId === "Business") {
          setStep(5);
        }
      } else {
        setEmailData(null);
        setEmailType("business");
        setClientExist(false);
        setStep(2);
      }
    } catch {
      setEmailFetchSent(true);
      setEmailFetchFailed(true);
      setEmailData(null);
      setEmailType(null);
      setClientExist(false);
    } finally {
      setLoading(false);
    }
  }, [mainEmail]);

  const handleEmailSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    checkEmailExists();
  };

  const handleCrmTypeSubmit = () => {
    if (selectedCrmType === "passenger") {
      setStep(3);
    } else {
      setStep(5);
    }
  };

  // Contact info helpers
  const contactPerson =
    emailData?.locationInformation?.[0]?.contactPersonsList?.[0];
  const location = emailData?.locationInformation?.[0];

  const visualIndex = stepToVisualIndex(step);
  const backStep = getBackStep(step);
  // For existing clients on step 5, don't show back (they skipped type selection)
  const canGoBack = backStep !== null && !(step === 5 && clientExist);

  return (
    <section ref={sectionRef} className={styles.section}>
      {/* ── Sticky Stepper Bar ── */}
      {step < 6 && (
        <div className={styles.stepperBar}>
          <div className={styles.stepperInner}>
            {/* Back button */}
            <button
              type="button"
              className={styles.stepBackBtn}
              disabled={!canGoBack}
              onClick={() => canGoBack && setStep(backStep!)}
              title="Go back"
            >
              <i className="fa-solid fa-arrow-left" />
            </button>

            {/* Steps */}
            <div className={styles.stepsTrack}>
              {STEPS.map((s, idx) => {
                const isPassed = visualIndex > idx;
                const isActive = visualIndex === idx;
                const cls = [
                  styles.stepItem,
                  isPassed ? styles.stepPassed : "",
                  isActive ? styles.stepActive : "",
                ]
                  .filter(Boolean)
                  .join(" ");

                return (
                  <div key={idx} style={{ display: "contents" }}>
                    {idx > 0 && (
                      <div
                        className={`${styles.stepConnector} ${visualIndex >= idx ? styles.stepConnectorDone : ""}`}
                      />
                    )}
                    <div className={cls}>
                      <div className={styles.stepCircle}>
                        {isPassed ? (
                          <i
                            className={`fa-solid fa-check ${styles.stepCheckIcon}`}
                          />
                        ) : (
                          idx + 1
                        )}
                      </div>
                      <div className={styles.stepText}>
                        <span className={styles.stepTitle}>{s.title}</span>
                        <span className={styles.stepSubtitle}>
                          {s.subtitle}
                        </span>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>

            {/* Save button — desktop only */}
            {step === 5 && quoteFormValid && (
              <button
                type="submit"
                form="quote-form"
                className={styles.stepSaveBtn}
                title="Submit Request"
              >
                <i className="fa-solid fa-paper-plane" />
                <span>Submit</span>
              </button>
            )}
          </div>
        </div>
      )}

      {/* ── Mobile fixed submit bar ── */}
      {step === 5 && quoteFormValid && (
        <div className={styles.mobileSubmitBar}>
          <button type="submit" form="quote-form" className={styles.mobileSubmitBtn}>
            <i className="fa-solid fa-paper-plane" />
            Submit Request
          </button>
        </div>
      )}

      {/* ── Page Body ── */}
      <div className={styles.sectionBody}>
        {step < 6 && (
          // Step 1 is the landing, so it earns the hero treatment. Steps 2-5
          // keep the compact heading — once you're inside the wizard the height
          // is better spent on the form than on a restated title.
          <div
            className={`${styles.heading} ${step === 1 ? styles.headingHero : ""}`}
          >
            {step === 1 && (
              <span className={styles.headingIcon}>
                <i className="fa-solid fa-compass" aria-hidden="true" />
              </span>
            )}
            <h1>Request a Quote</h1>
            <p>Get a personalized travel quote tailored to you.</p>
          </div>
        )}

      <div className="container">
        {/* ── Step 1: Email ── */}
        {step === 1 && (
          <div className={styles.card}>
            {emailFetchFailed && emailFetchSent && (
              <div className={`${styles.alert} ${styles.alertDanger}`}>
                <i className="fa-solid fa-circle-xmark" />
                Something went wrong. Please try again later.
              </div>
            )}
            <form onSubmit={handleEmailSubmit}>
              <div className={styles.formGroup}>
                <label className={styles.formLabel} htmlFor="main_email">
                  Please Enter Your Email Address To Continue{" "}
                  <span style={{ color: "#ef4444" }}>*</span>
                </label>
                <div className={styles.inputIconWrap}>
                  <i className={`fa-regular fa-envelope ${styles.inputIcon}`} />
                  <input
                    id="main_email"
                    type="email"
                    className={`${styles.formInput} ${styles.formInputLg} ${styles.formInputWithIcon} ${emailTouched && !emailValid ? styles.formInputError : ""}`}
                    placeholder="Enter Your Email Address *"
                    value={mainEmail}
                    onChange={(e) => setMainEmail(e.target.value)}
                    onFocus={() => setEmailTouched(true)}
                  />
                </div>
                {emailTouched && mainEmail && !emailValid && (
                  <span className={styles.fieldError}>
                    Please enter a valid email address
                  </span>
                )}
              </div>
              <button
                type="submit"
                className={`${styles.btnPrimary} ${styles.btnPrimaryLg}`}
                disabled={loading || !mainEmail || !emailValid}
              >
                {loading && <span className={styles.spinner} />}
                Next
                {!loading && <i className="fa-solid fa-chevron-right" />}
              </button>
            </form>
          </div>
        )}

        {/* ── Step 2: Select Type ── */}
        {step === 2 && (
          <div className={styles.card}>
            <h5 className={styles.sectionTitle}>
              Select Your Type <span style={{ color: "#ef4444" }}>*</span>
            </h5>
            <div className={styles.typeCards}>
              <button
                type="button"
                className={`${styles.typeCard} ${selectedCrmType === "business" ? styles.typeCardActive : ""}`}
                onClick={() => setSelectedCrmType("business")}
              >
                <div className={styles.typeCardIcon}>
                  <i className="fa-solid fa-briefcase" />
                </div>
                <span className={styles.typeCardTitle}>Business</span>
                <span className={styles.typeCardDesc}>Agency, operator, association & more</span>
                <div className={styles.typeCardCheck}>
                  <i className="fa-solid fa-circle-check" />
                </div>
              </button>
              <button
                type="button"
                className={`${styles.typeCard} ${selectedCrmType === "passenger" ? styles.typeCardActive : ""}`}
                onClick={() => setSelectedCrmType("passenger")}
              >
                <div className={styles.typeCardIcon}>
                  <i className="fa-solid fa-user" />
                </div>
                <span className={styles.typeCardTitle}>Passenger</span>
                <span className={styles.typeCardDesc}>Individual traveler</span>
                <div className={styles.typeCardCheck}>
                  <i className="fa-solid fa-circle-check" />
                </div>
              </button>
            </div>
            <div style={{ marginTop: 20, textAlign: "right" }}>
              <button
                type="button"
                className={styles.btnPrimary}
                style={{ width: "auto" }}
                disabled={loading || !selectedCrmType}
                onClick={handleCrmTypeSubmit}
              >
                Next <i className="fa-solid fa-chevron-right" />
              </button>
            </div>
          </div>
        )}

        {/* ── Step 3: Passenger redirect ── */}
        {step === 3 && (
          <div className={styles.card}>
            <div className={`${styles.alert} ${styles.alertInfo}`}>
              Thank you for your interest in our products, please get in touch
              with your Travel Agency or you can find the nearest Travel Agency
              that can assist you with your Travel arrangements by clicking the
              link below.
            </div>
            <div style={{ textAlign: "center", margin: "16px 0 8px" }}>
              <Link href={AppRoutes.findAgent()} className={styles.btnOutline} title="Find your nearest travel agent">
                <i className="fa-solid fa-map-marker-alt" />
                Find Nearest Agent
              </Link>
            </div>
          </div>
        )}

        {/* ── Step 4: Email exists — Passenger ── */}
        {step === 4 && (
          <div className={styles.card}>
            <div className={styles.infoBox}>
              <p style={{ margin: "0 0 12px", fontSize: "0.88rem" }}>
                Thank you for your interest in our products, our records
                indicate that you are related to{" "}
                <strong>{emailData?.name ?? "Unknown"}</strong>{" "}
                <strong>{emailData?.type ?? "Agency"}</strong>
              </p>
              <hr className={styles.divider} />
              <div>
                {location?.tnAddress?.full_Address && (
                  <div className={styles.infoItem}>
                    <i className="fa-solid fa-map-marker-alt" />
                    <span>
                      <strong>Address:</strong>{" "}
                      {location.tnAddress.full_Address}
                    </span>
                  </div>
                )}
                {contactPerson?.email && (
                  <div className={styles.infoItem}>
                    <i className="fa-solid fa-envelope" />
                    <span>
                      <strong>Email:</strong>{" "}
                      <a
                        href={`mailto:${contactPerson.email}`}
                        title={`Email ${contactPerson.email}`}
                      >
                        {contactPerson.email}
                      </a>
                    </span>
                  </div>
                )}
                {contactPerson?.businessPhone && (
                  <div className={styles.infoItem}>
                    <i className="fa-solid fa-phone" />
                    <span>
                      <strong>Business Phone:</strong>{" "}
                      <a
                        href={`tel:${contactPerson.businessPhone}`}
                        title={`Call ${sanitizePhone(contactPerson.businessPhone)}`}
                      >
                        {sanitizePhone(contactPerson.businessPhone)}
                      </a>
                    </span>
                  </div>
                )}
              </div>
            </div>
            <p style={{ fontSize: "0.85rem", color: "#374151" }}>
              If you want to contact your nearest agent directly instead, please
              click the link below.
            </p>
            <Link href={AppRoutes.findAgent()} className={styles.btnOutline} title="Find your nearest travel agent">
              <i className="fa-solid fa-map-marker-alt" />
              Find Nearest Agent
            </Link>
          </div>
        )}

        {/* ── Step 5: Business Quote Form ── */}
        {step === 5 && (
          <ClientQuoteForm
            email={mainEmail}
            clientExist={clientExist}
            guestId={guestId}
            data={emailData}
            utm={utmParams}
            referrer={referrer}
            destinationsInRegions={destinations}
            destinationsWithPackages={destinationsWithPackages}
            onStepChange={setStep}
            onValidChange={setQuoteFormValid}
          />
        )}

        {/* ── Step 6: Success ── */}
        {step === 6 && (
          <div className={styles.card}>
            <div className={styles.resultCard}>
              {/* eslint-disable-next-line @next/next/no-img-element */}
              <img
                className={styles.resultImage}
                src="/registration_successful.svg"
                alt="Success"
                width={250}
              />
              <h4>Request Sent Successfully!</h4>
              <p>
                Thank you for the opportunity. <br />
                Your lead will be assigned to one of our Destination Specialists.
              </p>
              <Link
                href={AppRoutes.home()}
                className={styles.btnPrimary}
                style={{ width: "auto", display: "inline-flex" }}
                title="Continue to Central Holidays home page"
              >
                <i className="fa-solid fa-home" />
                Continue to Website
              </Link>
            </div>
          </div>
        )}

        {/* ── Step 7: Error ── */}
        {step === 7 && (
          <div className={styles.card}>
            <div className={styles.resultCard}>
              {/* eslint-disable-next-line @next/next/no-img-element */}
              <img
                className={styles.resultImage}
                src="/register_error.jpeg"
                alt="Error"
                width={250}
              />
              <h4>Registration Failed</h4>
              <p>
                There is a problem with your registration. <br />
                If the problem persists, please feel free to contact us.
              </p>
              <Link
                href={AppRoutes.contact()}
                className={styles.btnPrimary}
                style={{ width: "auto", display: "inline-flex" }}
                title="Contact Central Holidays"
              >
                <i className="fa-solid fa-envelope" />
                Contact Us
              </Link>
            </div>
          </div>
        )}
      </div>
      </div>{/* end sectionBody */}
    </section>
  );
}
